import styled from '@emotion/styled';
import {
  BrowserMessageType,
  sendElectronMessage,
} from 'electron-communication-lib/BrowserForApp';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  EHeaderButton,
  EList,
  EListActiveEntry,
  EListItemEntry,
  EUndecoratedButton,
} from 'webapp-lib/Components';
import {useDrop} from 'webapp-lib/Functions';
import {
  mixinFlexboxRow,
  mixinFloatingSideBar,
  mixinUploadHighlight,
} from 'webapp-lib/styling';
import {classy} from 'webapp-lib/utils';

import {Clipboard, MoveDocument} from '_/assets/images';
import {DropInfoBox} from '_/components/DropInfoBox';
import {
  ADDITIONAL_FILESHELF_INFO,
  CASUS_DATA_TRANSFER_KEY,
  CASUS_DISPLAYNAME_TRANSFER_KEY,
  DOCUMENT_DATA_TRANSFER_KEY,
  DOCUMENT_NAME_TRANSFER_KEY,
  DOCUMENT_TYPE_TRANSFER_KEY,
  EMAIL_DATA_TRANSFER_KEY,
  EMAIL_SUBJECT_TRANSFER_KEY,
} from '_/constants/dragData';

import {FileListComponent} from './FileListItem';
import {FileInformation} from './FileShelf.types';

export const FileShelf = ({
  isOpen,
  disabled,
  setIsOpen,
}: {
  isOpen: boolean;
  disabled?: boolean;
  setIsOpen: (open: boolean) => void;
}): React.ReactElement => {
  const {t} = useTranslation('documents');
  const [filesList, setFilesList] = useState<FileInformation[]>([]);
  const [activeItem, setActiveItem] = useState<EListActiveEntry>();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const {dropCallbacks, isDragging: isDraggingOver} = useDrop({
    dataKeys: [],
    onDropAccept: (dataList) => {
      const casusIds = dataList[CASUS_DATA_TRANSFER_KEY]?.split(',');
      const casusNames = dataList[CASUS_DISPLAYNAME_TRANSFER_KEY]?.split(',');
      const documentTypes = dataList[DOCUMENT_TYPE_TRANSFER_KEY]?.split(',');
      const fileNames = dataList[DOCUMENT_NAME_TRANSFER_KEY]?.split(',');
      const subjects = dataList[EMAIL_SUBJECT_TRANSFER_KEY]?.split(',');
      const additionalInfos = dataList[ADDITIONAL_FILESHELF_INFO]?.split(',');
      const documentLoadList = dataList[DOCUMENT_DATA_TRANSFER_KEY]?.split(
        ','
      ).reduce(
        (
          map: Record<
            string,
            {
              casusId: string;
              casusDisplayNameWithClient: string;
              displayName: string;
              type: string;
              additionalInformation: string;
            }
          >,
          documentId: string,
          index: number
        ) => ({
          ...map,
          [documentId]: {
            casusId: casusIds[index],
            casusDisplayNameWithClient: casusNames[index],
            displayName: fileNames[index],
            type: documentTypes[index],
            additionalInformation: additionalInfos[index],
          },
        }),
        {}
      );

      const emailLoadList = dataList[EMAIL_DATA_TRANSFER_KEY]?.split(
        ','
      ).reduce(
        (
          map: Record<
            string,
            {
              casusId?: string;
              casusDisplayNameWithClient?: string;
              displayName: string;
              type: string;
              additionalInformation: string;
            }
          >,
          emailId: string,
          index: number
        ) => ({
          ...map,
          [emailId]: {
            casusId: casusIds[index] || undefined,
            casusDisplayNameWithClient: casusNames[index] || undefined,
            displayName: subjects[index],
            additionalInformation: additionalInfos[index],
            type: 'EMAIL',
          },
        }),
        {}
      );

      sendElectronMessage({
        payload: {...(documentLoadList ?? {}), ...(emailLoadList ?? {})},
        type: BrowserMessageType.FILE_SHELF_DOWNLOAD_FILES,
      });
    },
  });

  const handleSelectAll = useCallback(() => {
    setSelectedItems(
      filesList
        .filter((file) => file.state === 'DOWNLOADED')
        .map((file) => file.fileId)
    );
  }, [filesList]);

  const handleDeleteAll = useCallback(() => {
    sendElectronMessage({
      payload: filesList.map((fileInfo) => fileInfo.fileId),
      type: BrowserMessageType.FILE_SHELF_DELETE_FILES,
    });
  }, [filesList]);

  const handleFilesResponse = useCallback((e: MessageEvent) => {
    if (
      e.data.type === 'renderer' &&
      e.data?.payload.type === 'get-files-in-shelf-response'
    ) {
      setFilesList(e.data?.payload.files);
    }
  }, []);

  const filesInShelf = useMemo(
    () =>
      filesList.map(
        (fileInfo: FileInformation): EListItemEntry<FileInformation> => ({
          id: fileInfo.fileId,
          raw: fileInfo,
          Component: FileListComponent,
          options: {
            disableDragStyle: true,
            isSelectable: fileInfo.state === 'DOWNLOADED',
            isActivatable: fileInfo.state === 'DOWNLOADED',
            onDragStart:
              fileInfo.state === 'DOWNLOADED'
                ? (evt) => {
                    evt.preventDefault();
                    const files = [
                      ...new Set([
                        ...selectedItems
                          .map(
                            (selectedId) =>
                              filesList.find(
                                (file) =>
                                  file.fileId === selectedId &&
                                  fileInfo.state === 'DOWNLOADED'
                              )?.path
                          )
                          .filter((filter) => filter),
                        fileInfo.path,
                      ]),
                    ];
                    // rule disabled because the api elements on window gets defined by electron
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    window.api?.startDrag(files);
                  }
                : undefined,
          },
        })
      ),
    [filesList, selectedItems]
  );

  useEffect(() => {
    window.addEventListener('message', handleFilesResponse);
    return () => window.removeEventListener('message', handleFilesResponse);
  }, [handleFilesResponse]);

  useEffect(() => {
    let id = 0;
    if (isOpen) {
      id = window.setInterval(() => {
        sendElectronMessage({
          payload: null,
          type: BrowserMessageType.FILE_SHELF_GET_FILES,
        });
        // poll the files every 0.75 seconds, this should feel snappy enough
      }, 750);
    }
    // remove interval when new effect gets triggered
    return () => (id ? clearInterval(id) : undefined);
  }, [isOpen]);

  return (
    <>
      <EHeaderButton
        contentFlag={
          filesList.length > 0
            ? {
                content: filesList.length.toString(),
                mode: 'informative',
              }
            : undefined
        }
        disabled={disabled}
        isActive={isOpen}
        title={t('common:headerButtons.fileShelf')}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Clipboard />
      </EHeaderButton>
      {isOpen && (
        <SideBar
          {...dropCallbacks}
          className={classy({dragging: isDraggingOver})}
        >
          <ListHeader>
            <EUndecoratedButton onClick={handleSelectAll}>
              {t('fileList.selectAll')}
            </EUndecoratedButton>
            <EUndecoratedButton onClick={handleDeleteAll}>
              {t('fileList.deleteAll')}
            </EUndecoratedButton>
          </ListHeader>

          <EList
            activeItem={activeItem}
            data={filesInShelf}
            placeholder={t('fileList.placeholder')}
            selectedItems={selectedItems}
            onActiveItemChange={setActiveItem}
            onSelectedItemsChange={setSelectedItems}
          />

          {isDraggingOver ? (
            <DropInfoBox>
              <MoveDocument />
              {t('fileshelf.dropAreaInfoBoxMultiType')}
            </DropInfoBox>
          ) : null}
        </SideBar>
      )}
    </>
  );
};

export const SideBar = styled.div`
  ${mixinFloatingSideBar}

  &.dragging {
    ${mixinUploadHighlight}
    z-index: var(--zindex-sidebar-dropzone-decoration);
  }
`;

const ListHeader = styled.div`
  ${mixinFlexboxRow}
  padding: var(--gutter-small);
  border-bottom: var(--regular-border-width) solid var(--color-core-primary);
`;
